/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
'use client';

import type { OrderDetail } from '@ahm/api-wrappers-core';
import { useAuth } from '@ahm/react-auth';
import { toast } from '@ahm/react-ui';
import {
  Box,
  Button,
  CloseButton,
  Divider,
  Group,
  Radio,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  Textarea,
  rem,
} from '@mantine/core';
import { closeAllModals, type ContextModalProps } from '@mantine/modals';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { ModalName, showModal } from '@/components/modals';
import styles from '@/components/RadioButton.module.css';
import { useCancelIntercityOrder } from '@/features/booking/api/intercity';
import type {
  ProofOfWork,
  QuickAction,
} from '@/features/booking/api/master-data/types';
import { useGetProofOfWork } from '@/features/booking/api/master-data/use-get-proof-of-work';
import { useCancelOrder } from '@/features/booking/api/order/use-cancel-order';
import { useCountCancelledOrdersToday } from '@/features/booking/api/order/use-count-cancelled-orders-today';
import { ORDER_CANCEL_ACTION_TYPE } from '@/lib/constants';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { checkPartnerFromToken, reuseOrder } from '@/lib/utils';

export type CancelReasonListModalProps = ContextModalProps<{
  order: OrderDetail;
}>;

const MIN_INPUT_LENGTH = 10;
const MAX_INPUT_LENGTH = 200;
const MAX_CANCEL_ORDER_IN_DAY = 10;

export function CancelReasonListModal({
  innerProps: { order },
}: CancelReasonListModalProps) {
  const isMobile = useIsMobile();
  const router = useRouter();
  const queryClient = useQueryClient();
  const tCancel = useTranslations('record.order_cancellation');
  const { session } = useAuth();
  const [selectedReason, setSelectedReason] = useState<ProofOfWork | null>(
    null
  );
  const [hasInput, setHasInput] = useState<boolean>(false);
  const [inputDescription, setInputDescription] = useState<string>('');
  const [validationError, setValidationError] = useState<boolean>(false);
  const [hasUserTyped, setHasUserTyped] = useState<boolean>(false);

  const validActionTypes = useMemo(
    () => [
      ORDER_CANCEL_ACTION_TYPE.REORDER,
      ORDER_CANCEL_ACTION_TYPE.REBROADCAST,
      ORDER_CANCEL_ACTION_TYPE.CHANGE_INFO,
    ],
    []
  );

  const {
    data: proofOfWorkList,
    isLoading: isLoadingProofOfWork,
    error,
  } = useGetProofOfWork({
    user_type: 'user',
    reason_type: 'poc',
    status: order.status,
    service_id: order.service_id,
    delivery_type: order.delivery_type || order.service?.delivery_type,
  });

  const isPartner = useMemo(() => {
    return session?.token ? checkPartnerFromToken(session.token) : false;
  }, [session?.token]);

  const isNationwide = useMemo(() => {
    return Boolean(order._id.startsWith('ICT'));
  }, [order._id]);

  const { count: numCancelledOrderToday } = useCountCancelledOrdersToday(
    order.status
  );

  const handleCancelSuccess = () => {
    closeAllModals();
    toast.success({
      title: 'Hủy đơn hàng thành công',
      message: 'Đơn hàng của bạn đã được hủy thành công',
      withIcon: true,
    });

    // Invalidate the record detail query to trigger a refetch
    void queryClient.invalidateQueries({
      queryKey: ['order-detail', order._id],
    });
  };

  const handleCancelError = (error: any) => {
    toast.error({
      title: 'Hủy đơn hàng thất bại',
      message:
        error.response?.data?.message || 'Có lỗi xảy ra, vui lòng thử lại',
      withIcon: true,
    });
  };

  const { mutate: cancelOrder, isPending: isCancelling } = useCancelOrder({
    onSuccess: handleCancelSuccess,
    onError: handleCancelError,
  });

  const { mutate: cancelIntercityOrder, isPending: isCancellingIntercity } =
    useCancelIntercityOrder({
      onSuccess: handleCancelSuccess,
      onError: handleCancelError,
    });

  const handleCancelOrder = (action: QuickAction | null = null) => {
    if (!selectedReason) return;

    if (numCancelledOrderToday > MAX_CANCEL_ORDER_IN_DAY) {
      closeAllModals();
      showModal(
        ModalName.WarningOverCancelledOrder,
        { numCancelledOrderToday },
        {
          centered: true,
          fullScreen: false,
          withCloseButton: false,
          closeOnClickOutside: false,
          closeOnEscape: false,
          styles: {
            body: {
              background: 'white',
            },
          },
        }
      );
      return;
    }

    const body: any = {
      order_id: order._id,
      cancel_code: selectedReason.code,
      comment: inputDescription.trim(),
    };

    if (action && action.action_type === ORDER_CANCEL_ACTION_TYPE.REBROADCAST) {
      body.rebroadcast = true;
    }

    const options = {
      isPartner,
    };

    if (isNationwide) {
      const nationwideParams = {
        order_id: order._id,
        cancel_code: body.cancel_code,
        cancel_comment: body.comment,
      };
      cancelIntercityOrder(nationwideParams);
    } else {
      cancelOrder({ body, options });
    }
  };

  const handleSelectReason = (item: ProofOfWork) => {
    setSelectedReason(item);

    const isRequiresInput = item.requires?.includes('input_description');
    if (isRequiresInput) {
      setHasInput(true);
      // Don't set validation error immediately, wait for user to type
      setValidationError(false);
      setHasUserTyped(false);
    } else {
      setHasInput(false);
      setInputDescription('');
      setValidationError(false);
      setHasUserTyped(false);
    }
  };

  const handleInputChange = (value: string) => {
    setInputDescription(value);
    setHasUserTyped(true);

    if (hasInput && selectedReason?.requires?.includes('input_description')) {
      setValidationError(
        !value.trim() || value.trim().length < MIN_INPUT_LENGTH
      );
    }
  };

  const cancelReasonList = useMemo(() => {
    if (!proofOfWorkList) return [];

    const withInputDescription = proofOfWorkList.filter((item) =>
      item.requires?.includes('input_description')
    );
    const withoutInputDescription = proofOfWorkList.filter(
      (item) => !item.requires?.includes('input_description')
    );

    return [...withoutInputDescription, ...withInputDescription];
  }, [proofOfWorkList]);

  const isDisabled = useMemo(() => {
    return (
      !selectedReason ||
      (hasInput &&
        (!inputDescription.trim() ||
          inputDescription.trim().length < MIN_INPUT_LENGTH)) ||
      Boolean(validationError) ||
      isCancelling
    );
  }, [
    selectedReason,
    hasInput,
    inputDescription,
    validationError,
    isCancelling,
  ]);

  const handleQuickAction = async (action: QuickAction) => {
    await handleCancelOrder(action);

    if (
      action.action_type === ORDER_CANCEL_ACTION_TYPE.REORDER ||
      action.action_type === ORDER_CANCEL_ACTION_TYPE.CHANGE_INFO
    ) {
      reuseOrder(order, router);
    }
  };

  if (error) {
    return (
      <Stack align="center" justify="center" p={rem(32)} gap={rem(16)}>
        <Text c="red" fw={500}>
          {error.message || 'Không thể tải danh sách lý do hủy'}
        </Text>
        <Text size="sm" c="dimmed" ta="center">
          Đã xảy ra lỗi khi tải dữ liệu. Vui lòng thử lại sau.
        </Text>
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.location.reload()}
        >
          Thử lại
        </Button>
      </Stack>
    );
  }

  return (
    <Stack h={isMobile ? '100vh' : 'auto'} gap={0}>
      <Box pos="sticky" top={0} bg="white" zIndex={1}>
        <Group justify="space-between" align="center" p={rem(16)}>
          <Text fz={rem(20)} lh={rem(24)} fw={700}>
            {tCancel('cancel_reason')}
          </Text>
          <CloseButton
            iconSize={rem(24)}
            variant="transparent"
            onClick={closeAllModals}
            c="neutral.6"
          />
        </Group>
        <Divider />
      </Box>

      <Stack flex={1} mih={isMobile ? 0 : rem(400)}>
        {isLoadingProofOfWork ? (
          <Stack gap={rem(8)} p={rem(16)}>
            <Skeleton height={rem(40)} />
            <Skeleton height={rem(40)} width="80%" />
            <Skeleton height={rem(40)} width="60%" />
          </Stack>
        ) : (
          <ScrollArea flex={1} h={isMobile ? 'auto' : 404}>
            <Radio.Group name="cancelReason" value={selectedReason?.code}>
              <Stack gap={0}>
                {cancelReasonList?.map((item, idx) => (
                  <Stack key={item._id} gap={0}>
                    <Button
                      component="div"
                      fullWidth
                      variant="subtle"
                      color="neutral"
                      h={rem(48)}
                      px={rem(16)}
                      radius={0}
                      styles={{
                        inner: {
                          justifyContent: 'flex-start',
                        },
                        label: {
                          width: '100%',
                          flex: '1 1 auto',
                        },
                      }}
                      onClick={() => handleSelectReason(item)}
                    >
                      <Radio
                        classNames={{ radio: styles.radioBtn }}
                        value={item.code}
                        label={item.title_vi}
                        variant="outline"
                        w="100%"
                        labelPosition="left"
                        styles={{
                          body: {
                            width: '100%',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          },
                          inner: { alignSelf: 'center' },
                        }}
                      />
                    </Button>

                    {idx !== cancelReasonList.length - 1 ? (
                      <Divider mx={rem(16)} />
                    ) : null}

                    {hasInput && selectedReason?._id === item._id && (
                      <Stack gap={rem(4)} px={rem(16)}>
                        <Textarea
                          placeholder={tCancel('input_placeholder')}
                          value={inputDescription}
                          onChange={(e) => handleInputChange(e.target.value)}
                          error={
                            hasUserTyped && validationError ? true : undefined
                          }
                          minRows={4}
                          maxLength={MAX_INPUT_LENGTH}
                          styles={{
                            input: {
                              minHeight: rem(124),
                            },
                          }}
                        />

                        <Group justify="space-between" align="center">
                          <Text
                            c={
                              hasUserTyped && validationError
                                ? 'red.6'
                                : 'neutral.6'
                            }
                            fz={rem(12)}
                            fw={500}
                            lh={rem(16)}
                          >
                            Tối thiểu {MIN_INPUT_LENGTH} ký tự
                          </Text>

                          <Text fz={rem(12)} fw={500} lh={rem(16)}>
                            {inputDescription.length}/{MAX_INPUT_LENGTH}
                          </Text>
                        </Group>
                      </Stack>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Radio.Group>
          </ScrollArea>
        )}
      </Stack>

      <Stack p={rem(16)} gap={rem(12)} pos="sticky" bottom={0} w="100%">
        {selectedReason?.quick_actions
          ?.filter((action) => {
            return (
              validActionTypes.includes(action.action_type as any) &&
              action.enable
            );
          })
          .map((action, idx) => (
            <Stack key={idx} gap={rem(4)}>
              <Text size="xs">{action.guide_line?.title_vi}</Text>
              <Button
                fullWidth
                h={rem(48)}
                fz={rem(14)}
                fw={700}
                lh={rem(20)}
                onClick={() => handleQuickAction(action)}
              >
                {action.title_vi}
              </Button>
            </Stack>
          ))}

        <Button
          fullWidth
          variant="light"
          bg={isDisabled ? 'neutral.1' : 'red.2'}
          c={isDisabled ? 'neutral.5' : 'red.6'}
          h={rem(48)}
          fz={rem(14)}
          fw={700}
          lh={rem(20)}
          onClick={handleCancelOrder}
          disabled={isDisabled}
          loading={isCancelling || isCancellingIntercity}
        >
          {tCancel('cancel_btn')}
        </Button>
      </Stack>
    </Stack>
  );
}
